import axios from "../../../apis/Api";
import history, { logout } from "../../../history";
import { toast } from "react-toastify";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  GET_SCR_SETTINGS_ADJUSTMENT_TYPE_DEFINITIONS,
  GET_SCR_SETTINGS_ADJUSTMENT_TYPES,
  GET_SCR_SETTINGS_PAYMENT_TYPE_DEFINITIONS,
  GET_SCR_SETTINGS_PAYMENT_TYPES,
  PATCH_SCR_SETTINGS_ADJUSTMENT_TYPE,
  PATCH_SCR_SETTINGS_PAYMENT_TYPE,
  UPSERT_SCR_SETTINGS_ADJUSTMENT_TYPE,
  UPSERT_SCR_SETTINGS_PAYMENT_TYPE,
} from "../../../Types";

const typesListCache = {
  payment: null,
  adjustment: null,
};

let paymentTypesAbortController = null;
let adjustmentTypesAbortController = null;
const definitionRequestsInFlight = new Map();

export const clearSCRSettingsTypesCache = () => {
  typesListCache.payment = null;
  typesListCache.adjustment = null;
};

const handleRequestError = (error) => {
  if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
    return;
  }

  if (error.response) {
    if (error.response.status === 401) {
      logout();
      history.push("/login");
      history.go("/login");
      return;
    }

    if (error.response.data) {
      toast.error(
        error.response.data.Message
          ? error.response.data.Message
          : error.response.data.message
      );
      return;
    }
  }

  toast.error("Error while processing the request");
};

const ensureSuccess = (data) => {
  if (data && data.isSuccess === false) {
    toast.error(data.message || "Error while processing the request");
    return false;
  }

  return true;
};

const requestWithToast =
  (request, successMessage, onSuccess, { showGlobalLoading = true } = {}) =>
  async (dispatch) => {
    if (showGlobalLoading) {
      dispatch(showLoading());
    }

    try {
      const { data } = await request();
      if (!ensureSuccess(data)) {
        return null;
      }

      if (onSuccess) {
        dispatch(onSuccess(data.response));
      }

      if (successMessage) {
        toast.success(successMessage);
      }

      return data.response ?? true;
    } catch (error) {
      handleRequestError(error);
      return null;
    } finally {
      if (showGlobalLoading) {
        dispatch(hideLoading());
      }
    }
  };

const silentGet =
  (request, onSuccess) => async (dispatch) => {
    try {
      const { data } = await request();
      if (!ensureSuccess(data)) {
        return null;
      }

      if (onSuccess) {
        dispatch(onSuccess(data.response));
      }

      return data.response ?? null;
    } catch (error) {
      handleRequestError(error);
      return null;
    }
  };

const buildTypesUrl = (endpoint, distributorId, includeInactive, includeDefinitions) =>
  `${endpoint}?distributorId=${distributorId}&includeInactive=${includeInactive}&includeDefinitions=${includeDefinitions}`;

export const getSCRPaymentTypes =
  (distributorId, includeInactive = true, { force = false, silent = true } = {}) =>
  async (dispatch) => {
    if (!distributorId) {
      return null;
    }

    if (!force && typesListCache.payment === distributorId) {
      return null;
    }

    if (paymentTypesAbortController) {
      paymentTypesAbortController.abort();
    }

    paymentTypesAbortController = new AbortController();
    const { signal } = paymentTypesAbortController;

    const fetchTypes = silentGet(
      () =>
        axios.get(
          buildTypesUrl(
            "api/SCR/NonVizLinkPaymentType",
            distributorId,
            includeInactive,
            false
          ),
          { signal }
        ),
      (response) => ({
        type: GET_SCR_SETTINGS_PAYMENT_TYPES,
        payload: response || [],
      })
    );

    const result = await dispatch(fetchTypes);
    if (result !== null && !signal.aborted) {
      typesListCache.payment = distributorId;
    }

    return result;
  };

export const createSCRPaymentType = (model) => async (dispatch) => {
  const result = await dispatch(
    requestWithToast(
      () => axios.post("api/SCR/NonVizLinkPaymentType", model),
      "Payment type created successfully",
      (response) => ({
        type: UPSERT_SCR_SETTINGS_PAYMENT_TYPE,
        payload: { ...response, definitionCount: response?.definitionCount ?? 0 },
      })
    )
  );

  if (result) {
    typesListCache.payment = model.distributorId;
  }

  return result;
};

export const updateSCRPaymentType = (id, model) => async (dispatch) => {
  const result = await dispatch(
    requestWithToast(
      () => axios.put(`api/SCR/NonVizLinkPaymentType/${id}`, model),
      "Payment type updated successfully",
      (response) => ({
        type: UPSERT_SCR_SETTINGS_PAYMENT_TYPE,
        payload: response,
      })
    )
  );

  return result;
};

export const toggleSCRPaymentTypeActive = (id, isActive) => async (dispatch) => {
  const result = await dispatch(
    requestWithToast(
      () =>
        axios.patch(
          `api/SCR/NonVizLinkPaymentType/${id}/toggle-active/${isActive}`
        ),
      `Payment type ${isActive ? "activated" : "deactivated"} successfully`,
      (response) => ({
        type: PATCH_SCR_SETTINGS_PAYMENT_TYPE,
        payload: response,
      }),
      { showGlobalLoading: false }
    )
  );

  return result;
};

export const getSCRPaymentTypeDefinitions =
  (paymentTypeId, includeInactive = false) =>
  async (dispatch) => {
    if (!paymentTypeId) {
      return null;
    }

    const cacheKey = `${paymentTypeId}:${includeInactive}`;
    if (definitionRequestsInFlight.has(cacheKey)) {
      return definitionRequestsInFlight.get(cacheKey);
    }

    const requestPromise = dispatch(
      silentGet(
        () =>
          axios.get(
            `api/SCR/NonVizLinkPaymentType/${paymentTypeId}/definitions?includeInactive=${includeInactive}`
          ),
        (response) => ({
          type: GET_SCR_SETTINGS_PAYMENT_TYPE_DEFINITIONS,
          payload: {
            typeId: paymentTypeId,
            definitions: response || [],
          },
        })
      )
    ).finally(() => {
      definitionRequestsInFlight.delete(cacheKey);
    });

    definitionRequestsInFlight.set(cacheKey, requestPromise);
    return requestPromise;
  };

export const createSCRPaymentTypeDefinition =
  (paymentTypeId, model) => async (dispatch) =>
    dispatch(
      requestWithToast(
        () =>
          axios.post(
            `api/SCR/NonVizLinkPaymentType/${paymentTypeId}/definitions`,
            model
          ),
        "Field created successfully",
        null,
        { showGlobalLoading: false }
      )
    );

export const updateSCRPaymentTypeDefinition =
  (paymentTypeId, definitionId, model) => async (dispatch) =>
    dispatch(
      requestWithToast(
        () =>
          axios.put(
            `api/SCR/NonVizLinkPaymentType/${paymentTypeId}/definitions/${definitionId}`,
            model
          ),
        "Field updated successfully",
        null,
        { showGlobalLoading: false }
      )
    );

export const toggleSCRPaymentTypeDefinitionActive =
  (paymentTypeId, definitionId, isActive) => async (dispatch) =>
    dispatch(
      requestWithToast(
        () =>
          axios.patch(
            `api/SCR/NonVizLinkPaymentType/${paymentTypeId}/definitions/${definitionId}/toggle-active/${isActive}`
          ),
        `Field ${isActive ? "activated" : "deactivated"} successfully`,
        null,
        { showGlobalLoading: false }
      )
    );

export const getSCRAdjustmentTypes =
  (distributorId, includeInactive = true, { force = false, silent = true } = {}) =>
  async (dispatch) => {
    if (!distributorId) {
      return null;
    }

    if (!force && typesListCache.adjustment === distributorId) {
      return null;
    }

    if (adjustmentTypesAbortController) {
      adjustmentTypesAbortController.abort();
    }

    adjustmentTypesAbortController = new AbortController();
    const { signal } = adjustmentTypesAbortController;

    const fetchTypes = silentGet(
      () =>
        axios.get(
          buildTypesUrl(
            "api/SCR/InvoiceAdjustmentType",
            distributorId,
            includeInactive,
            false
          ),
          { signal }
        ),
      (response) => ({
        type: GET_SCR_SETTINGS_ADJUSTMENT_TYPES,
        payload: response || [],
      })
    );

    const result = await dispatch(fetchTypes);
    if (result !== null && !signal.aborted) {
      typesListCache.adjustment = distributorId;
    }

    return result;
  };

export const createSCRAdjustmentType = (model) => async (dispatch) => {
  const result = await dispatch(
    requestWithToast(
      () => axios.post("api/SCR/InvoiceAdjustmentType", model),
      "Adjustment type created successfully",
      (response) => ({
        type: UPSERT_SCR_SETTINGS_ADJUSTMENT_TYPE,
        payload: { ...response, definitionCount: response?.definitionCount ?? 0 },
      })
    )
  );

  if (result) {
    typesListCache.adjustment = model.distributorId;
  }

  return result;
};

export const updateSCRAdjustmentType = (id, model) => async (dispatch) =>
  dispatch(
    requestWithToast(
      () => axios.put(`api/SCR/InvoiceAdjustmentType/${id}`, model),
      "Adjustment type updated successfully",
      (response) => ({
        type: UPSERT_SCR_SETTINGS_ADJUSTMENT_TYPE,
        payload: response,
      })
    )
  );

export const toggleSCRAdjustmentTypeActive = (id, isActive) => async (dispatch) =>
  dispatch(
    requestWithToast(
      () =>
        axios.patch(
          `api/SCR/InvoiceAdjustmentType/${id}/toggle-active/${isActive}`
        ),
      `Adjustment type ${isActive ? "activated" : "deactivated"} successfully`,
      (response) => ({
        type: PATCH_SCR_SETTINGS_ADJUSTMENT_TYPE,
        payload: response,
      }),
      { showGlobalLoading: false }
    )
  );

export const getSCRAdjustmentTypeDefinitions =
  (adjustmentTypeId, includeInactive = false) =>
  async (dispatch) => {
    if (!adjustmentTypeId) {
      return null;
    }

    const cacheKey = `${adjustmentTypeId}:${includeInactive}`;
    if (definitionRequestsInFlight.has(cacheKey)) {
      return definitionRequestsInFlight.get(cacheKey);
    }

    const requestPromise = dispatch(
      silentGet(
        () =>
          axios.get(
            `api/SCR/InvoiceAdjustmentType/${adjustmentTypeId}/definitions?includeInactive=${includeInactive}`
          ),
        (response) => ({
          type: GET_SCR_SETTINGS_ADJUSTMENT_TYPE_DEFINITIONS,
          payload: {
            typeId: adjustmentTypeId,
            definitions: response || [],
          },
        })
      )
    ).finally(() => {
      definitionRequestsInFlight.delete(cacheKey);
    });

    definitionRequestsInFlight.set(cacheKey, requestPromise);
    return requestPromise;
  };

export const createSCRAdjustmentTypeDefinition =
  (adjustmentTypeId, model) => async (dispatch) =>
    dispatch(
      requestWithToast(
        () =>
          axios.post(
            `api/SCR/InvoiceAdjustmentType/${adjustmentTypeId}/definitions`,
            model
          ),
        "Field created successfully",
        null,
        { showGlobalLoading: false }
      )
    );

export const updateSCRAdjustmentTypeDefinition =
  (adjustmentTypeId, definitionId, model) => async (dispatch) =>
    dispatch(
      requestWithToast(
        () =>
          axios.put(
            `api/SCR/InvoiceAdjustmentType/${adjustmentTypeId}/definitions/${definitionId}`,
            model
          ),
        "Field updated successfully",
        null,
        { showGlobalLoading: false }
      )
    );

export const toggleSCRAdjustmentTypeDefinitionActive =
  (adjustmentTypeId, definitionId, isActive) => async (dispatch) =>
    dispatch(
      requestWithToast(
        () =>
          axios.patch(
            `api/SCR/InvoiceAdjustmentType/${adjustmentTypeId}/definitions/${definitionId}/toggle-active/${isActive}`
          ),
        `Field ${isActive ? "activated" : "deactivated"} successfully`,
        null,
        { showGlobalLoading: false }
      )
    );
